// Keep these utility functions in theme.js
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

// Favorite products functionality
// Storage key for favorites
const FAVORITES_STORAGE_KEY = 'komono-favorites';

function getFavorites() {
  const favoritesJson = localStorage.getItem(FAVORITES_STORAGE_KEY);
  return favoritesJson ? JSON.parse(favoritesJson) : [];
}

function saveFavorites(favorites) {
  localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
}

/**
 * Toggle a product in favorites
 * Can be used with onclick="toggleFavorite('product-handle', this)"
 * 
 * @param {string} productHandle - The product handle to toggle
 * @param {HTMLElement} buttonElement - The button element that was clicked (optional)
 * @returns {boolean} - Whether the product is now favorited
 */
function toggleFavorite(productHandle, buttonElement) {
  console.log('Toggling favorite for:', productHandle);
  if (!productHandle) return false;
  
  const favorites = getFavorites();
  const isFavorite = favorites.includes(productHandle);
  
  if (isFavorite) {
    // Remove from favorites
    const index = favorites.indexOf(productHandle);
    favorites.splice(index, 1);
    
    // Update button if provided
    if (buttonElement) {
      buttonElement.classList.remove('is-favorite');
      // If button has aria-label, update it
      if (buttonElement.hasAttribute('aria-label')) {
        buttonElement.setAttribute('aria-label', 'Add to favorites');
      }
    }
  } else {
    // Add to favorites
    favorites.push(productHandle);
    
    // Update button if provided
    if (buttonElement) {
      buttonElement.classList.add('is-favorite');
      // If button has aria-label, update it
      if (buttonElement.hasAttribute('aria-label')) {
        buttonElement.setAttribute('aria-label', 'Remove from favorites');
      }
    }
  }
  
  saveFavorites(favorites);

  updateFavoriteButtonsStatus();

  // if on a product page, update all product favorite buttons to be sure
  if (window.location.pathname.includes('/products/')) {
    // also update the product page button if it exists
    const productPageButton = document.querySelector('.product__favorite-button');
    const isFavorite = favorites.includes(productHandle);
    if (productPageButton && productPageButton.getAttribute('data-product-handle') !== productHandle) {
      return !isFavorite; // exit if not matching
    } else {
      console.log('productPageButton handle matches current product');
      if (!isFavorite) {
        productPageButton.classList.remove('is-favorite');
        if (productPageButton.hasAttribute('aria-label')) {
          productPageButton.setAttribute('aria-label', 'Add to favorites');
        }
      } else {
        productPageButton.classList.add('is-favorite');
        if (productPageButton.hasAttribute('aria-label')) {
          productPageButton.setAttribute('aria-label', 'Remove from favorites');
        }
      }
    }
  }
  
  // Return new state
  return !isFavorite;
}

/**
 * Update all favorite buttons on the page based on favorites in local storage
 * This should be called on page load
 */
function updateFavoriteButtonsStatus() {
  const favorites = getFavorites();
  if (favorites.length === 0) return;
  
  // Find all product cards with data-product-handle attribute
  const productCards = document.querySelectorAll('[data-product-handle]');
  
  productCards.forEach(card => {
    const productHandle = card.getAttribute('data-product-handle');
    
    if (productHandle && favorites.includes(productHandle)) {
      const favoriteButton = card.querySelector('.card-product__favorite');
      if (favoriteButton) {
        favoriteButton.classList.add('is-favorite');
        // If button has aria-label, update it
        if (favoriteButton.hasAttribute('aria-label')) {
          favoriteButton.setAttribute('aria-label', 'Remove from favorites');
        }
      }
    } else {
      const favoriteButton = card.querySelector('.card-product__favorite');
      if (favoriteButton) {
        favoriteButton.classList.remove('is-favorite');
        // If button has aria-label, update it
        if (favoriteButton.hasAttribute('aria-label')) {
          favoriteButton.setAttribute('aria-label', 'Add to favorites');
        }
      }
    }
  });
  
  // Also update any standalone favorite buttons
  const standaloneButtons = document.querySelectorAll('[data-product-favorite]');
  standaloneButtons.forEach(button => {
    const productHandle = button.getAttribute('data-product-handle');
    
    if (productHandle && favorites.includes(productHandle)) {
      button.classList.add('is-favorite');
      // If button has aria-label, update it
      if (button.hasAttribute('aria-label')) {
        button.setAttribute('aria-label', 'Remove from favorites');
      }
    }
  });
}

/**
 * Check if a product is favorited
 * @param {string} productHandle - The product handle to check
 * @returns {boolean} - Whether the product is favorited
 */
function isProductFavorited(productHandle) {
  if (!productHandle) return false;
  const favorites = getFavorites();
  return favorites.includes(productHandle);
}

/**
 * Global function to quickly add a product variant to cart
 * Can be used with onclick="quickAdd('variant-id', this)"
 * 
 * @param {string} variantId - The variant ID to add
 * @param {HTMLElement} buttonElement - The button element that was clicked (optional)
 * @returns {Promise} - Promise that resolves when the item is added
 */
window.quickAdd = function(variantId, buttonElement) {
  if (!variantId) {
    console.error('No variant ID provided for quick add');
    return Promise.reject('No variant ID');
  }
  
  // Show loading state if button provided
  if (buttonElement) {
    buttonElement.classList.add('loading');
    buttonElement.disabled = true;
  }
  
  // Create form data
  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', 1);
  
  // Add to cart via AJAX
  return fetch('/cart/add.js', {
    method: 'POST',
    body: formData
  })
  .then(response => {
    if (!response.ok) {
      return response.json().then(data => {
        throw new Error(data.description || 'Error adding to cart');
      });
    }
    return response.json();
  })
  .then(data => {
    // Success - update button state
    if (buttonElement) {
      buttonElement.classList.remove('loading');
      buttonElement.classList.add('added');
      buttonElement.disabled = true;
    }
    
    // Find cart drawer and set flag to switch tabs
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.shouldSwitchToCartTab = true;
      setTimeout(() => {
        if (cartDrawer.switchToCartTab) cartDrawer.switchToCartTab();
      }, 300);
    }
    
    // Update cart and open drawer
    window.refreshCart(true);
    
    return data;
  })
  .catch(error => {
    console.error('Error adding to cart:', error);
    if (buttonElement) {
      buttonElement.classList.remove('loading');
      buttonElement.disabled = false;
      
      // Show error message as tooltip
      showQuickAddError(buttonElement, error.message || 'Error adding to cart');
    }
    return Promise.reject(error);
  });
};

// Initialize favorite buttons on page load
document.addEventListener('DOMContentLoaded', function() {
  updateFavoriteButtonsStatus();
});

// Function to show error message as tooltip
function showQuickAddError(button, message) {
  // Create tooltip element if it doesn't exist
  let tooltip = document.querySelector('.quick-add-error-tooltip');
  if (!tooltip) {
    tooltip = document.createElement('div');
    tooltip.classList.add('quick-add-error-tooltip');
    document.body.appendChild(tooltip);
  }
  
  // Set message and position
  tooltip.textContent = message;
  const buttonRect = button.getBoundingClientRect();
  tooltip.style.top = `${buttonRect.top - tooltip.offsetHeight - 10}px`;
  tooltip.style.left = `${buttonRect.left + buttonRect.width/2 - tooltip.offsetWidth/2}px`;
  tooltip.classList.add('active');
  
  // Hide after 3 seconds
  setTimeout(() => {
    tooltip.classList.remove('active');
  }, 3000);
}

// Function to check product availability and update UI
function checkProductAvailability(productHandle, variantId) {
  if (!productHandle || !variantId) return;
  
  fetch(`/products/${productHandle}.js`)
    .then(response => response.json())
    .then(product => {
      const variant = product.variants.find(v => v.id == variantId);
      if (variant) {
        const available = variant.available;
        const inventory = variant.inventory_quantity;
        const inventoryManagement = variant.inventory_management;
        
        // Update quick add buttons with this variant ID
        const quickAddButtons = document.querySelectorAll(`[data-quick-add][data-variant-id="${variantId}"]`);
        quickAddButtons.forEach(button => {
          if (!available) {
            button.classList.add('sold-out');
            button.disabled = true;
            button.removeAttribute('data-quick-add');
            button.setAttribute('aria-label', 'Sold out');
          } else if (inventoryManagement && inventory <= 5) {
            // Low inventory
            button.setAttribute('data-low-inventory', inventory);
            
            // Optional: Add low inventory badge
            const lowInventoryBadge = document.createElement('span');
            lowInventoryBadge.classList.add('quick-add-low-inventory');
            // lowInventoryBadge.textContent = `Only ${inventory} left`;
            button.appendChild(lowInventoryBadge);
          }
        });
        
        // Update add to cart buttons on product page
        const addToCartButtons = document.querySelectorAll(`.product-form input[type="submit"][value="Add to cart"]`);
        addToCartButtons.forEach(button => {
          const form = button.closest('form');
          if (form && form.querySelector(`[name="id"][value="${variantId}"]`)) {
            if (!available) {
              button.disabled = true;
              button.value = 'Sold Out';
              button.classList.add('sold-out');
            } else if (inventoryManagement && inventory <= 5) {
              // Add low inventory warning
              let inventoryWarning = form.querySelector('.product-form__inventory-warning');
              if (!inventoryWarning) {
                inventoryWarning = document.createElement('div');
                inventoryWarning.classList.add('product-form__inventory-warning');
                form.querySelector('.product-form__buttons-wrapper').insertAdjacentElement('beforebegin', inventoryWarning);
              }
              // inventoryWarning.textContent = `Only ${inventory} left in stock`;
            }
          }
        });
      }
    })
    .catch(error => {
      console.error('Error checking product availability:', error);
    });
}

// Global function to initialize quick-add buttons
window.initQuickAddButtons = function(container = document) {
  const buttons = container.querySelectorAll('[data-quick-add]:not([data-quick-add-initialized])');
  
  buttons.forEach(button => {
    // Create a fresh clone to remove any existing listeners
    const clone = button.cloneNode(true);
    if (button.parentNode) {
      button.parentNode.replaceChild(clone, button);
    } else {
      return; // Skip if button has no parent
    }
    
    // Mark as initialized to avoid duplicate event listeners
    clone.setAttribute('data-quick-add-initialized', 'true');
    
    // Get variant inventory if available
    const variantId = clone.getAttribute('data-variant-id');
   
    if (variantId) {
      fetch(`/products/dummy?variant=${variantId}&section_id=inventory-data`)
        .then(response => response.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const inventoryData = doc.querySelector('[data-variant-inventory]');
          
          if (inventoryData) {
            const quantity = parseInt(inventoryData.getAttribute('data-variant-inventory'));
            
            // If inventory is 0, disable the button
            if (quantity <= 0) {
              clone.classList.add('sold-out');
              clone.disabled = true;
              clone.setAttribute('aria-label', 'Sold out');
              
              // Remove quick-add functionality
              clone.removeAttribute('data-quick-add');
              return;
            }
            
            // If low inventory, add a data attribute
            if (quantity <= 5) {
              clone.setAttribute('data-low-inventory', quantity);
            }
          }
        })
        .catch(err => console.error('Error checking inventory:', err));
    }

    clone.addEventListener('click', function(event) {
      event.preventDefault();
      event.stopPropagation();
      
      const productId = this.getAttribute('data-product-id');
      const variantId = this.getAttribute('data-variant-id');
      
      if (!variantId) {
        console.error('No variant ID found for quick add');
        return;
      }
      
      // Show loading state
      this.classList.add('loading');
      this.disabled = true;
      
      // Create form data
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      
      // Add to cart via AJAX
      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(response => {
        // Check for inventory errors
        if (!response.ok) {
          return response.json().then(errorData => {
            throw new Error(errorData.description || 'Error adding to cart');
          });
        }
        return response.json();
      })
      .then(data => {
        // Success - update button state
        this.classList.remove('loading');
        this.classList.add('added');
        this.disabled = true;
        
        // Find cart drawer and set flag to switch tabs
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.shouldSwitchToCartTab = true;
          
          // Direct tab switch with timeout - crucial for first page load
          setTimeout(() => {
            if (cartDrawer.switchToCartTab) {
              cartDrawer.switchToCartTab();
            }
          }, 300);
        }
        
        // Update cart and open drawer
        window.refreshCart(true);
        
        // Backup tab switch with longer delay
        setTimeout(() => {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && drawer.switchToCartTab) {
            drawer.switchToCartTab();
          }
        }, 800);
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        this.classList.remove('loading');
        this.disabled = false;
        
        // Show error message as tooltip
        showQuickAddError(this, error.message || 'Error adding to cart');
      });
    });
  });
};

// Quick Add functionality for product cards
class QuickAdd {
  constructor() {
    this.buttons = document.querySelectorAll('[data-quick-add]');
    this.init();
  }

  init() {
    this.buttons.forEach(button => {
      button.addEventListener('click', this.handleQuickAdd.bind(this));
    });
  }

  handleQuickAdd(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const productId = button.getAttribute('data-product-id');
    const variantId = button.getAttribute('data-variant-id');
    
    if (!variantId) {
      console.error('No variant ID found for quick add');
      return;
    }
    
    // Show loading state
    button.classList.add('loading');
    
    // Create form data
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    
    // Add to cart via AJAX
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      // Success - update button state
      button.classList.remove('loading');
      
      // Update cart and open drawer using existing functions
      window.refreshCart(true);
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      button.classList.remove('loading');
    });
  }
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(e) {
    e.preventDefault();

    // Works across Safari/mobile: always resolve the actual button
    const button = e.currentTarget || e.target.closest('button[data-action]');
    if (!button) return;

    const action = button.dataset.action; // "plus" | "minus"
    const previousValue = this.input.value;

    if (action === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else if (action === 'minus') {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }

    if (this.input.dataset.min === previousValue && action === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

const imagesLazyLoadEffect = () => {
  // Initialize all images
  const optimalImages = document.querySelectorAll('.optimal-image');
  
  optimalImages.forEach(img => {
    // Set sizes attribute
    if (img.dataset.sizes) {
      img.sizes = img.dataset.sizes;
    }
    
    // Set srcset attribute
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
    }
    
    // Set src attribute to the appropriate size
    if (img.dataset.src) {
      img.src = img.dataset.src.replace('1x1', '300x');
    }
    
    // Mark as loaded
    img.classList.add('optimal-image--loaded');
  });
};

// Function to handle product form submissions via AJAX
class ProductForm {
  constructor(form) {
    this.form = form;
    this.init();
  }

  init() {
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  handleSubmit(event) {
    event.preventDefault();
    const formData = new FormData(this.form);
    
    // Show loading state
    const submitButton = this.form.querySelector('input[type="submit"], button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      if (submitButton.tagName === 'INPUT') {
        this.originalButtonValue = submitButton.value;
        submitButton.value = 'Adding...';
      } else {
        this.originalButtonText = submitButton.textContent;
        submitButton.textContent = 'Adding...';
      }
    }
    
    this.submitForm(formData, submitButton);
  }

  submitForm(formData, submitButton) {
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    })
    .then((response) => {
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.description || 'Error adding to cart');
        });
      }
      return response.json();
    })
    .then((data) => {
      // Success
      console.log('Success:', data);

      // Update cart UI and open cart drawer
      window.refreshCart(true);
      
      // Update button state
      if (submitButton) {
        submitButton.disabled = false;
        if (submitButton.tagName === 'INPUT') {
          submitButton.value = 'Added to cart!';
        } else {
          submitButton.textContent = 'Added to cart!';
        }
        
        // Reset button text after delay
        setTimeout(() => {
          if (submitButton.tagName === 'INPUT') {
            submitButton.value = this.originalButtonValue || 'Add to cart';
          } else {
            submitButton.textContent = this.originalButtonText || 'Add to cart';
          }
        }, 2000);
      }
    })
    .catch((error) => {
      console.error('Error:', error);
      
      // Reset button state
      if (submitButton) {
        submitButton.disabled = false;
        if (submitButton.tagName === 'INPUT') {
          submitButton.value = this.originalButtonValue || 'Add to cart';
        } else {
          submitButton.textContent = this.originalButtonText || 'Add to cart';
        }
      }
      
      // Show error message
      let errorContainer = this.form.querySelector('.product-form__error');
      if (!errorContainer) {
        errorContainer = document.createElement('div');
        errorContainer.classList.add('product-form__error');
        if (submitButton) {
          submitButton.insertAdjacentElement('afterend', errorContainer);
        } else {
          this.form.appendChild(errorContainer);
        }
      }
      
      errorContainer.textContent = error.message;
      errorContainer.classList.add('active');
      
      // Hide after 5 seconds
      setTimeout(() => {
        errorContainer.classList.remove('active');
      }, 5000);
    });
  }
}

const initProductForms = () => {
  const productForms = document.querySelectorAll('form.shopify-product-form');
  if (!productForms.length) return;
  productForms.forEach((form) => {
    new ProductForm(form);
  });
};

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

document.addEventListener('DOMContentLoaded', () => {
  // Initialize product forms
  initProductForms();

  // Initialize quick add buttons
  window.initQuickAddButtons();
  
  // Image lazy loading
  // imagesLazyLoadEffect();

 // Check if we're on the post-login redirect page
  if (window.location.search.includes('customer_logged_in=true')) {
    // Customer just logged in - trigger sync
    localStorage.setItem('customer_logged_in', 'true');
    if (typeof syncFavorites === 'function') {
      syncFavorites();
    }
  }
  
  // Use Shopify's customer section rendering events
  document.addEventListener('shopify:section:load', function(event) {
    if (event.detail.sectionId === 'header' || event.detail.sectionId.includes('customer-account')) {
      // Check if customer state changed
      const wasLoggedIn = localStorage.getItem('customer_logged_in') === 'true';
      const isLoggedIn = document.querySelector('.customer-logged-in') !== null;
      
      if (!wasLoggedIn && isLoggedIn) {
        // Customer logged in
        localStorage.setItem('customer_logged_in', 'true');
        if (typeof syncFavorites === 'function') {
          syncFavorites();
        }
      } else if (wasLoggedIn && !isLoggedIn) {
        // Customer logged out
        localStorage.setItem('customer_logged_in', 'false');
      }
    }
  });
});